import { Breadcrumbs, Flex, IconFont, Input, Popover, SelectDropdown } from '@byecode/ui'
import { getBlockIcon, getBlockName } from '@lighthouse/block'
import type { BlockAbstract, BlockType } from '@lighthouse/core'
import { findBlockById, LinkDocument, useAtomAction, useAtomData } from '@lighthouse/shared'
import { useAtomCallback } from 'jotai/utils'
import React, { useCallback, useMemo, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { pageSyncComponentsAtom, syncComponentsAtom } from '@/atoms/application/state'
import { setSelectedNodesAtom } from '@/atoms/page/action'
import {
    lastPageOfStackAtom,
    pageAtomFamily,
    pageInternalStateAtomFamily,
    selectedBlockContextAtom,
    selectedBlockPathAtom
} from '@/atoms/page/state'
import type { NodeIdWithScope } from '@/atoms/page/types'
import { AsideType } from '@/atoms/page/types'
import { useCurrentBreakPointId, useMainBreakPoint } from '@/hooks/useApplication'
import { useBlockActions } from '@/hooks/useBlock'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

const EditIcon = styled(IconFont)`
    opacity: 0;
    margin-left: 2px;
`
const StyledBreadcrumbs = styled(Breadcrumbs)`
    &:hover {
        ${EditIcon} {
            opacity: 1;
        }
    }
`

export const PageBreadcrumbs: React.FC = () => {
    const mainBreakPoint = useMainBreakPoint()
    const pageMeta = useAtomData(lastPageOfStackAtom)
    const { pageId = '', stackId = '' } = pageMeta ?? {}
    const { asideType } = useAtomData(pageInternalStateAtomFamily(stackId))
    const selectedBlock = useAtomData(
        selectedBlockContextAtom,
        useCallback(s => s?.block, [])
    )
    const selectedBlockPath = useAtomData(selectedBlockPathAtom)

    const pageName = useAtomData(
        pageAtomFamily(pageId),
        useCallback(s => s?.name || '无标题', [])
    )
    const disabledWithVersion = useIsDisabledWithVersion()

    const { run: setSelectedNodes } = useAtomAction(setSelectedNodesAtom)

    const { onUpdateBlock } = useBlockActions(pageId, stackId)

    const [documentType, subNodeType] = useMemo(() => {
        if (selectedBlock) {
            if (selectedBlock.type === 'view') {
                return [selectedBlock.type, selectedBlock.config.viewType]
            }
            if (selectedBlock.type === 'chart') {
                return [selectedBlock.type, selectedBlock.config.chartType]
            }
            if (selectedBlock.type === 'field') {
                return [selectedBlock.type, selectedBlock.config.inputType]
            }
            return [selectedBlock.type]
        }
        if (asideType) {
            return ['navbar' as unknown as BlockType]
        }
        return []
    }, [asideType, selectedBlock])

    const [editBlock, setEditBlock] = useState<BlockAbstract | null>(null)

    useUpdateEffect(() => {
        setEditBlock(null)
    }, [selectedBlock])

    const { run: setSyncComponents } = useAtomAction(syncComponentsAtom)
    const breakPointId = useCurrentBreakPointId()

    const handleUpdateBlockName = useCallback(
        (value: string) => {
            setEditBlock(null)

            if (!editBlock || !value) {
                return
            }

            const label = editBlock.title || getBlockName(editBlock)
            if (value === label) {
                return
            }

            if (editBlock.isLeafSynchronous || editBlock.isMasterSynchronous) {
                const syncBlockId = editBlock.synchronousId || editBlock.id
                setSyncComponents(draft => {
                    const draftBlock = findBlockById(syncBlockId, draft)
                    if (!draftBlock) {
                        return
                    }

                    draftBlock.title = value
                })
            } else {
                onUpdateBlock({ nextBlock: { ...editBlock, title: value }, prevBlock: editBlock })
            }
        },
        [editBlock, onUpdateBlock, setSyncComponents]
    )

    const breadcrumbs = useMemo(() => {
        switch (asideType) {
            case AsideType.BLOCK: {
                if (editBlock) {
                    const label = editBlock.title || getBlockName(editBlock)
                    return [
                        {
                            label: (
                                <Input
                                    width="100%"
                                    disabled={disabledWithVersion}
                                    size="sm"
                                    defaultValue={label}
                                    onBlur={e => {
                                        const v = e.currentTarget.value
                                        handleUpdateBlockName(v)
                                    }}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            const v = e.currentTarget.value
                                            handleUpdateBlockName(v)
                                        }
                                    }}
                                    autoFocus
                                    autoSelect
                                />
                            )
                        }
                    ]
                }

                const needFold = selectedBlockPath.length > 3

                if (needFold) {
                    const foldOptions = selectedBlockPath.slice(1, -1).map(path => {
                        return {
                            label: path.block.title || getBlockName(path.block),
                            value: path.block.id,
                            scope: path.scope
                        }
                    })

                    const firstPath = selectedBlockPath[0]
                    const lastPath = selectedBlockPath[selectedBlockPath.length - 1]

                    return [
                        {
                            label: firstPath ? (
                                <span
                                    onClick={() => {
                                        setSelectedNodes({ stackId, selectedNodes: [{ id: firstPath.block.id, scope: firstPath.scope }] })
                                    }}
                                >
                                    {firstPath.block.title || getBlockName(firstPath.block)}
                                </span>
                            ) : (
                                ''
                            )
                        },
                        {
                            label: (
                                <Popover width={180} withinPortal>
                                    <Popover.Target>
                                        <span>...</span>
                                    </Popover.Target>
                                    <Popover.Dropdown>
                                        <SelectDropdown
                                            options={foldOptions}
                                            onSelect={id => {
                                                const option = foldOptions.find(item => item.value === id)
                                                if (option) {
                                                    setSelectedNodes({
                                                        stackId,
                                                        selectedNodes: [{ id: option.value, scope: option.scope }]
                                                    })
                                                }
                                            }}
                                        />
                                    </Popover.Dropdown>
                                </Popover>
                            )
                        },
                        {
                            label: lastPath ? (
                                <Flex
                                    alignItems="center"
                                    gap={4}
                                    onClick={() => {
                                        if (lastPath.block.isMasterSynchronous) {
                                            return
                                        }
                                        setEditBlock(lastPath.block)
                                    }}
                                >
                                    <IconFont
                                        type={getBlockIcon(lastPath.block, breakPointId, mainBreakPoint)}
                                        size={16}
                                        color="var(--color-gray-400)"
                                    />
                                    <span>{lastPath.block.title || getBlockName(lastPath.block)}</span>
                                    {lastPath.block.isMasterSynchronous ? null : (
                                        <EditIcon type="edit" size={12} color="var(--color-gray-400)" />
                                    )}
                                </Flex>
                            ) : (
                                ''
                            )
                        }
                    ]
                }

                return selectedBlockPath.map((path, index, arr) => {
                    const { block, scope } = path

                    const label = block.title || getBlockName(block)
                    const isLast = index === arr.length - 1

                    if (isLast) {
                        return {
                            label: (
                                <Flex
                                    alignItems="center"
                                    gap={4}
                                    onClick={() => {
                                        if (block.isMasterSynchronous) {
                                            return
                                        }
                                        setEditBlock(block)
                                    }}
                                >
                                    <IconFont
                                        type={getBlockIcon(block, breakPointId, mainBreakPoint)}
                                        size={16}
                                        color="var(--color-gray-400)"
                                    />
                                    <span>{label}</span>
                                    {block.isMasterSynchronous ? null : <EditIcon type="edit" size={12} color="var(--color-gray-400)" />}
                                </Flex>
                            )
                        }
                    }

                    return {
                        label: (
                            <span
                                onClick={() => {
                                    setSelectedNodes({ stackId, selectedNodes: [{ id: block.id, scope }] })
                                }}
                            >
                                {label}
                            </span>
                        )
                    }
                })
            }

            case AsideType.NAVBAR: {
                return [{ label: '全局导航栏' }]
            }

            case AsideType.PAGE: {
                return [{ label: pageName }]
            }

            default: {
                break
            }
        }

        return []
    }, [
        asideType,
        editBlock,
        selectedBlockPath,
        disabledWithVersion,
        handleUpdateBlockName,
        breakPointId,
        mainBreakPoint,
        setSelectedNodes,
        stackId,
        pageName
    ])

    return (
        <StyledBreadcrumbs
            styles={{
                root: {
                    flex: 1
                },
                body: {
                    margin: '-8px 0'
                },
                item: {
                    cursor: 'pointer',
                    lineHeight: '22px',
                    width: editBlock ? '100%' : undefined
                },
                label: {
                    width: editBlock ? '100%' : undefined
                }
            }}
            separator=" / "
            items={breadcrumbs}
            extra={[<LinkDocument key="breadcrumbs_extra" type={documentType} subNodeType={subNodeType} />]}
        />
    )
}
